import {Component, OnInit, computed, inject, output, signal} from '@angular/core';
import {DatePipe} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {NgxOtpInputComponent, NgxOtpInputComponentOptions, NgxOtpStatus} from 'ngx-otp-input';
import {SecondFactorHttpService} from '../../../_auth/two-factor/second-factor-http.service';
import {RecoveryCodeSet, SecondFactorEnrolment, SecondFactorStatus} from '../../../_auth/two-factor/two-factor.models';
import {describeSecondFactorError} from '../../../_auth/two-factor/second-factor-errors';
import {GlobalAlertService} from '../../../_services/global-alert.service';
import {AlertLevel} from '../../../enums/alert-level';

/**
 * What the panel is currently asking of the person. One value, because these states are genuinely exclusive —
 * modelling them as independent booleans is how a screen ends up showing a QR code and a removal confirmation
 * at the same time.
 */
export type TwoFactorPanelMode =
  /** Showing the status. The only mode with no code entry on screen. */
  | 'idle'
  /** A pending enrolment: QR + secret shown, waiting for the code that confirms it. */
  | 'enrolling'
  /** Asking for a current code before replacing the recovery-code set. */
  | 'regenerating'
  /** Asking for a current code before removing the factor. */
  | 'removing'
  /** Showing a freshly issued recovery-code set. Terminal until acknowledged — see {@link TwoFactorPanelComponent.acknowledgeCodes}. */
  | 'codes';

/**
 * Self-service two-factor authentication for the signed-in account (#282) — the *Zwei-Faktor-Authentifizierung*
 * block of the profile page, and the working half of the two-factor remediation screen.
 *
 * **Why its own component rather than more of `ProfileComponent`.** It carries a five-state machine, four
 * endpoints and the one piece of genuinely irreversible UI in the app (see below); `ProfileComponent` is a
 * template-driven password form with no state of its own. Splitting it is also what lets it be *reused* by
 * `TwoFactorRequiredComponent` rather than reimplemented there — the enrolment flow being written twice, once
 * for the profile and once for the lockout screen, is exactly how the two would come to disagree about what
 * "enrolled" means.
 *
 * **The one rule that shapes the whole design: recovery codes are shown once.** The server does not store them
 * in a readable form and will never return the same set again, so between the response arriving and the person
 * writing them down there is no recovery path — not a reload, not a support call. Hence: the codes view is
 * modal within the panel (no other control is reachable from it), it cannot be dismissed by loading data or by
 * an error elsewhere, it states plainly that this is the only showing, and it offers both copy and download so
 * "I'll do it in a second" has somewhere to go. {@link enrolled} deliberately fires on the *acknowledgement*,
 * not on the confirmation — a caller that navigates away on "enrolment succeeded" would take the codes off the
 * screen before they were read.
 */
@Component({
  selector: 'app-two-factor-panel',
  standalone: true,
  imports: [DatePipe, FormsModule, NgxOtpInputComponent],
  templateUrl: './two-factor-panel.component.html'
})
export class TwoFactorPanelComponent implements OnInit {
  private readonly secondFactor = inject(SecondFactorHttpService);
  private readonly alert = inject(GlobalAlertService);

  /**
   * Fires when an **enrolment** has been confirmed *and* its recovery codes acknowledged — i.e. the account is
   * protected and the person has had their one look at the codes.
   *
   * Not emitted for a regeneration or a removal: those change the credential without changing whether one
   * exists, which is the only question a listener (the remediation screen, which reissues the session on it)
   * is asking. See the class remarks for why it is the acknowledgement and not the confirmation.
   */
  readonly enrolled = output<void>();

  /** The server's view of the factor. Null only while the first load is in flight, or after it failed. */
  readonly status = signal<SecondFactorStatus | null>(null);
  readonly mode = signal<TwoFactorPanelMode>('idle');
  /** The pending enrolment's QR/secret. Held only for the life of `mode === 'enrolling'`; never persisted. */
  readonly enrolment = signal<SecondFactorEnrolment | null>(null);
  /** The one and only showing of a recovery-code set. Never written to storage — see the class remarks. */
  readonly recoveryCodes = signal<string[] | null>(null);
  /** Which act produced {@link recoveryCodes} — the codes view says something different for each. */
  readonly codesOrigin = signal<'confirmed' | 'regenerated'>('confirmed');

  /** The initial status read is in flight. Distinct from {@link busy}: it decides whether there is a panel yet. */
  readonly loading = signal(true);
  /** A write (enrol/confirm/regenerate/remove) is in flight — disables the code entry and the buttons. */
  readonly busy = signal(false);
  /** The last failure, already translated to German. Cleared whenever a new attempt starts. */
  readonly errorMessage = signal<string | null>(null);
  /** Feedback for the copy button; reset on every new codes view. */
  readonly copied = signal(false);

  /** Drives the OTP control's success/failure styling without the panel owning its internals. */
  readonly otpStatus = signal<NgxOtpStatus | undefined>(undefined);

  /**
   * True while the person is proving the action with a **recovery code** rather than a code from the app.
   * Only ever set where {@link recoveryCodeAccepted} holds.
   */
  readonly usingRecoveryCode = signal(false);

  /**
   * The recovery code being typed.
   *
   * Named `…Entry`, not `recoveryCode`, on purpose: {@link recoveryCodes} — one letter away — is the issued SET
   * shown once and never stored. Two fields whose names differ by a plural, one of which must never be
   * persisted, is a mix-up waiting to happen in a template.
   */
  readonly recoveryCodeEntry = signal('');

  /** True while the company's policy makes this factor mandatory rather than optional. */
  readonly required = computed(() => this.status()?.required === true);

  /**
   * True when the company demands a **federated** sign-in, which enrolling an authenticator cannot satisfy.
   *
   * Called out separately because the two policies need opposite screens — one offers a button, the other has to
   * say that an administrator must act. That is the same distinction the backend draws by giving
   * `E_AUTH_ACCOUNT_SECURITY_LEVEL_UNREACHABLE` its own code instead of reusing `TWO_FACTOR_REQUIRED`.
   */
  readonly federatedRequired = computed(() => this.status()?.requiredLevel === 'Federated');

  /** "7 von 10", or null when no set has been issued (never enrolled). */
  readonly recoveryCodeCount = computed(() => {
    const s = this.status();
    if (!s || s.recoveryCodesIssued <= 0) return null;
    return `${s.remainingRecoveryCodes} von ${s.recoveryCodesIssued}`;
  });

  /**
   * True when the remaining codes are down to the last two.
   *
   * A threshold rather than "zero" on purpose: by the time the last code is spent the person is locked out of
   * the very screen that would issue new ones, so the warning has to arrive while they can still act on it.
   */
  readonly recoveryCodesLow = computed(() => {
    const s = this.status();
    return !!s && s.enabled && s.recoveryCodesIssued > 0 && s.remainingRecoveryCodes <= 2;
  });

  /**
   * Every issued code has been spent — the sharper end of {@link recoveryCodesLow}, and its own warning.
   *
   * Worth separating because at zero the "nur noch wenige übrig" wording is simply false beside a "0 von 10"
   * count, and because this is the state where the warning actually has to land: from here a lost phone means
   * no way back into the account at all.
   */
  readonly recoveryCodesExhausted = computed(() => {
    const s = this.status();
    return !!s && s.enabled && s.recoveryCodesIssued > 0 && s.remainingRecoveryCodes === 0;
  });

  /**
   * Whether the account has any **unused** recovery code left to type.
   *
   * `remainingRecoveryCodes` is the unused count — the server drops the matched hash on use — while
   * `recoveryCodesIssued` is the separate denominator behind "7 von 10". At zero the recovery route cannot
   * succeed, and offering it anyway produces a refusal that reads as "your code is wrong": the worst possible
   * thing to tell somebody who has already lost their phone.
   */
  readonly hasRecoveryCodes = computed(() => (this.status()?.remainingRecoveryCodes ?? 0) > 0);

  /**
   * Whether this action can be proven with a **recovery code** instead of a code from the app.
   *
   * Two conditions, both load-bearing:
   *  * the action is a removal or a regeneration — `/confirm` cannot take one, because no recovery codes exist
   *    yet at enrolment time;
   *  * the account still {@link hasRecoveryCodes}.
   *
   * The first is what keeps the lost-phone path open at all: without it somebody who signed in with a recovery
   * code could never remove the factor, therefore never enrol their new phone, and would spend the rest of
   * their printout on sign-ins until the account was gone. "Lost my phone → sign in with a code → remove →
   * re-enrol" only closes if removal takes the same kind of code the sign-in did.
   */
  readonly recoveryCodeAccepted = computed(() =>
    (this.mode() === 'regenerating' || this.mode() === 'removing') && this.hasRecoveryCodes());

  /**
   * The prompt above the code entry — one control, several quite different questions.
   *
   * Where a recovery code is accepted the prompt SAYS so, in both directions. Somebody whose phone is gone is
   * looking at six empty boxes; if nothing on screen mentions the printout in their hand, the reasonable
   * conclusion is that this screen cannot help them.
   */
  readonly codePrompt = computed(() => {
    const recovery = this.usingRecoveryCode();
    // The "a recovery code works here too" half is only appended where one could actually be typed — never on
    // an account whose codes are all spent, where it would send somebody hunting for a printout that is dead.
    const offer = this.recoveryCodeAccepted();
    switch (this.mode()) {
      case 'enrolling':
        return 'Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein, um die Einrichtung abzuschließen.';
      case 'regenerating':
        if (recovery) {
          return 'Geben Sie einen Ihrer bisherigen Wiederherstellungscodes ein, um neue Codes zu erzeugen.';
        }
        return 'Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein, um neue Wiederherstellungscodes '
          + 'zu erzeugen.'
          + (offer ? ' Kein Zugriff auf die App? Ein Wiederherstellungscode funktioniert hier ebenfalls.' : '');
      case 'removing':
        if (recovery) {
          return 'Geben Sie einen Ihrer Wiederherstellungscodes ein, um die Zwei-Faktor-Authentifizierung zu '
            + 'entfernen.';
        }
        return 'Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein, um die Zwei-Faktor-'
          + 'Authentifizierung zu entfernen.'
          + (offer
            ? ' Gerät verloren? Ein Wiederherstellungscode funktioniert hier ebenfalls — damit können Sie '
              + 'anschließend ein neues Gerät einrichten.'
            : '');
      default:
        return '';
    }
  });

  /**
   * Options for the shared `ngx-otp-input` control (already a dependency, already used by the mailing
   * submission screen — reused rather than hand-rolling six inputs and their focus/paste/backspace behaviour).
   *
   * `ariaLabels` is supplied explicitly: the control's own default labels are English, and this app has no i18n
   * layer, so a screen reader would otherwise read a German form in English. `inputMode: 'numeric'` is what puts
   * a phone keyboard on digits — the panel is used on phones and a QWERTY keyboard for a 6-digit code is the
   * kind of small friction that gets blamed on the code being wrong.
   */
  readonly otpOptions: NgxOtpInputComponentOptions = {
    otpLength: 6,
    autoFocus: true,
    inputMode: 'numeric',
    regexp: /^[0-9]+$/,
    ariaLabels: [
      'Ziffer 1 von 6', 'Ziffer 2 von 6', 'Ziffer 3 von 6',
      'Ziffer 4 von 6', 'Ziffer 5 von 6', 'Ziffer 6 von 6'
    ]
  };

  ngOnInit(): void {
    this.reload();
  }

  /** Re-reads the status. Never touches {@link mode}: a reload must not close a codes view the person is reading. */
  reload(): void {
    this.loading.set(true);
    this.secondFactor.status().subscribe({
      next: s => {
        this.status.set(s);
        this.loading.set(false);
      },
      error: err => {
        this.loading.set(false);
        this.errorMessage.set(describeSecondFactorError(
          err, 'Der Status der Zwei-Faktor-Authentifizierung konnte nicht geladen werden.'));
      }
    });
  }

  /**
   * Starts (or restarts) an enrolment.
   *
   * The previously shown secret is cleared *before* the request goes out, because the server replaces its
   * pending secret on every call: leaving the old QR on screen for the duration of the round-trip is an
   * invitation to scan a secret that has already been forgotten, which then fails confirmation with a
   * "wrong code" the person has no way to interpret.
   */
  startEnrolment(): void {
    if (this.busy()) return;
    this.beginAttempt();
    this.enrolment.set(null);
    this.busy.set(true);

    this.secondFactor.enrol().subscribe({
      next: e => {
        this.enrolment.set(e);
        this.mode.set('enrolling');
        this.busy.set(false);
      },
      error: err => this.failAttempt(err, 'Die Einrichtung konnte nicht gestartet werden.')
    });
  }

  /** Asks for a code before replacing the recovery-code set. */
  beginRegenerate(): void {
    this.beginAttempt();
    this.mode.set('regenerating');
  }

  /** Asks for a code before removing the factor. Never offered while `canDisable` is false. */
  beginRemove(): void {
    if (this.status()?.canDisable === false) return;
    this.beginAttempt();
    this.mode.set('removing');
  }

  /** Abandons whatever is being asked for and returns to the status view. Never reachable from the codes view. */
  cancel(): void {
    if (this.mode() === 'codes') return;
    this.beginAttempt();
    this.enrolment.set(null);
    this.mode.set('idle');
  }

  /** Submits the typed recovery code. Separate entry point only because its value comes from a signal. */
  submitRecoveryCode(): void {
    this.submitCode(this.recoveryCodeEntry());
  }

  /**
   * Handles a completed code entry — the single write path for all three code-guarded actions, and for both
   * kinds of code.
   *
   * Dispatching on {@link mode} rather than binding separate handlers keeps the "the code was wrong" behaviour
   * (mark the control failed, keep the mode, let them retype) in one place; copies of it are chances for one to
   * drop the person back to the status view with their enrolment abandoned. It also means a recovery code needs
   * no path of its own — the server takes either at `/remove` and `/recovery-codes`, so the only difference is
   * which control the string came from.
   */
  submitCode(code: string): void {
    if (this.busy()) return;
    const trimmed = code.trim();
    if (trimmed.length === 0) return;

    this.errorMessage.set(null);
    this.otpStatus.set(undefined);
    this.busy.set(true);

    switch (this.mode()) {
      case 'enrolling':
        this.secondFactor.confirm(trimmed).subscribe({
          next: set => this.showCodes(set, 'confirmed'),
          error: err => this.failAttempt(err, 'Der Code konnte nicht geprüft werden.')
        });
        return;
      case 'regenerating':
        this.secondFactor.regenerateRecoveryCodes(trimmed).subscribe({
          next: set => this.showCodes(set, 'regenerated'),
          error: err => this.failAttempt(err, 'Die Wiederherstellungscodes konnten nicht erneuert werden.')
        });
        return;
      case 'removing':
        this.secondFactor.remove(trimmed).subscribe({
          next: () => {
            this.busy.set(false);
            this.mode.set('idle');
            this.alert.createAndShow('Erfolg',
              'Die Zwei-Faktor-Authentifizierung wurde entfernt.', AlertLevel.success);
            this.reload();
          },
          error: err => this.failAttempt(err, 'Die Zwei-Faktor-Authentifizierung konnte nicht entfernt werden.')
        });
        return;
      default:
        this.busy.set(false);
    }
  }

  /**
   * Leaves the codes view — the acknowledgement that the set has been read.
   *
   * The only exit from `'codes'`, and the one place {@link enrolled} is emitted. Everything else that could
   * plausibly close this view (a status reload, an error elsewhere, a cancel) deliberately cannot.
   */
  acknowledgeCodes(): void {
    const wasEnrolment = this.codesOrigin() === 'confirmed';
    this.recoveryCodes.set(null);
    this.copied.set(false);
    this.mode.set('idle');
    if (wasEnrolment) this.enrolled.emit();
  }

  /**
   * Copies the codes to the clipboard.
   *
   * Guarded rather than assumed: `navigator.clipboard` is undefined on an insecure origin and can reject when
   * the document is not focused, and an unhandled rejection here would be a silent no-op on the one screen
   * where "I thought I had them" is unrecoverable. A failure therefore says so and points at the download.
   */
  copyCodes(): void {
    const text = this.codesAsText();
    if (!text) return;

    const clipboard = navigator.clipboard;
    if (!clipboard?.writeText) {
      this.errorMessage.set('Kopieren wird von diesem Browser nicht unterstützt. Bitte nutzen Sie '
        + '„Herunterladen“ oder notieren Sie die Codes.');
      return;
    }

    clipboard.writeText(text).then(
      () => {
        this.copied.set(true);
        this.errorMessage.set(null);
      },
      () => this.errorMessage.set('Die Codes konnten nicht kopiert werden. Bitte nutzen Sie „Herunterladen“.'));
  }

  /**
   * Offers the codes as a plain text file.
   *
   * An object URL rather than a `data:` URI: Safari refuses to download a `data:` href from a link click, and
   * this is the fallback for exactly the case where the clipboard is unavailable — a fallback that only works
   * in the browsers that did not need it is not one. Revoked immediately after the click; the download has
   * already been handed to the browser by then.
   */
  downloadCodes(): void {
    const text = this.codesAsText();
    if (!text) return;

    const url = URL.createObjectURL(new Blob([text], {type: 'text/plain;charset=utf-8'}));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'cheqme-wiederherstellungscodes.txt';
    link.click();
    URL.revokeObjectURL(url);
  }

  /** The codes as the copied/downloaded text: one per line, with a header naming what they are. */
  private codesAsText(): string | null {
    const codes = this.recoveryCodes();
    if (!codes?.length) return null;
    return ['CheqMe — Wiederherstellungscodes', '', ...codes, ''].join('\n');
  }

  /** Enters the codes view. Never called from anywhere but a successful confirm/regenerate. */
  private showCodes(set: RecoveryCodeSet, origin: 'confirmed' | 'regenerated'): void {
    this.busy.set(false);
    this.enrolment.set(null);
    // The code that got us here is spent (a recovery code is single-use). Dropping it stops a stale value
    // reappearing behind the codes view, and keeps a used credential out of memory for no reason.
    this.usingRecoveryCode.set(false);
    this.recoveryCodeEntry.set('');
    this.recoveryCodes.set(set.codes ?? []);
    this.codesOrigin.set(origin);
    this.copied.set(false);
    this.mode.set('codes');
    // Refreshes the counts behind the codes view, so acknowledging it lands on an accurate status rather than
    // the pre-enrolment one. Safe because `reload()` never touches `mode`.
    this.reload();
  }

  /**
   * Switches between the app code and a recovery code, clearing whatever was typed.
   *
   * Clearing is deliberate: carrying a half-typed value across the switch would leave six digits sitting in a
   * field expecting a 20-character code (or the reverse), and the resulting refusal would look like the code
   * being wrong rather than the field.
   */
  toggleRecoveryCode(useRecovery: boolean): void {
    // Turning it ON requires the route to be open; turning it OFF must ALWAYS work. A status reload that spends
    // the last code would otherwise strand somebody in a recovery entry with no way back to the app code.
    if (useRecovery && !this.recoveryCodeAccepted()) return;
    this.usingRecoveryCode.set(useRecovery);
    this.recoveryCodeEntry.set('');
    this.errorMessage.set(null);
    this.otpStatus.set(undefined);
  }

  /**
   * Clears the transient state every new attempt should start from — including the recovery-code entry.
   *
   * Resetting `usingRecoveryCode` here is what makes each action open on the app code: a removal abandoned in
   * recovery mode must not leave the *next* action's entry silently switched over.
   */
  private beginAttempt(): void {
    this.errorMessage.set(null);
    this.otpStatus.set(undefined);
    this.usingRecoveryCode.set(false);
    this.recoveryCodeEntry.set('');
  }

  /**
   * Turns a failed write into a German sentence and marks the code control as rejected, **keeping the mode** —
   * a wrong code must leave the person exactly where they were, with the entry still on screen.
   */
  private failAttempt(err: unknown, fallback: string): void {
    this.busy.set(false);
    this.otpStatus.set(NgxOtpStatus.FAILED);
    // A lock-out refuses app codes but still accepts a recovery code, so the message points at that route —
    // but only where it exists AND is not the route already being used. Somebody whose recovery code was just
    // answered `Locked` has been told their code did not match; "try a recovery code" is advice they are
    // already following.
    this.errorMessage.set(describeSecondFactorError(err, fallback, {
      recoveryCodeAvailable: this.recoveryCodeAccepted() && !this.usingRecoveryCode()
    }));
  }
}
